import { generateToast } from '@components/toasting'
import { envVars } from '@core/environment/env-vars'
import { IssuesModule } from '@core/modules/issues'
import { keyErrorInfo } from '@core/modules/issues/constants'
import { IntegrationsService, ScansService } from '@core/services/api-gateway'
import { Analytics } from '@core/services/mixpanel'
import { rsaEncrypt } from '@core/utils/encrypt'
import * as Sentry from '@sentry/react'
import {
    TAllIntegrations,
    TIntegration,
    TIntegrationActions,
    TIntegrationOrigin,
    TSecurityServiceName,
} from './integrations'
import {
    IntegrationRequestResponseData,
    SecurityServiceStateEnum,
} from '@atbay/productivity-integrations-node-sdk'
import { IntercomService } from '@/core/services/intercom/intercom.service'
import { AxiosError } from 'axios'
import { IntegrationsStore } from '@/core/store/Integrations'
import { AccountModule } from '../account/account.module'
import {
    TIntegrationProviders,
    TIntegrationProvider,
} from '@/core/store/Integrations/integrations.store'
import { IntegrationsModule } from '.'
import { TOAST_INTEGRATION_TYPE_MAPPING } from './constants'

const FRAUD_DEFENSE_INTEGRATION_TYPES = [
    'ms_fraud_defense',
    'google_fraud_defense',
]

const getProviderById = (id: string): TIntegrationProvider | undefined => {
    const { providers } = IntegrationsStore.getStore()
    const provider = providers[id]
    if (provider) {
        return provider
    }
    return undefined
}

const handleIntegrationLogin = async ({
    providerId,
    type,
    origin,
    action,
}: {
    providerId: string
    type: TAllIntegrations
    origin: TIntegrationOrigin
    action: TIntegrationActions
}) => {
    IntegrationsStore.setIntegrationEventData({
        action,
        origin,
    })

    if (action === 'add') {
        Analytics.event('SEM::user_clicks_at_install_integration', { origin })
    }

    await IntegrationsService.handleIntegrationLogin({
        providerId,
        type,
    })
}

const sendIntegrationCallEvent = ({
    providerId,
    type,
    origin,
    action,
}: {
    providerId: string
    type: TAllIntegrations
    origin: TIntegrationOrigin
    action: TIntegrationActions
}) => {
    const displayName = `${getProviderById(providerId)?.displayName ?? 'The'} integration`

    switch (action) {
        case 'add':
            Analytics.event('SEM::user_call_create_integration', {
                displayName,
                type,
                origin,
            })
            break
        case 'update':
            Analytics.event('SEM::user_call_update_integration', {
                displayName,
                type,
                origin,
            })
            break
        case 'reconnect':
            Analytics.event('SEM::user_call_reconnect_integration', {
                displayName,
                type,
                origin,
            })
            break
    }
    IntegrationsStore.setIntegrationEventData(null)
}

const sendIntegrationFailedEvent = ({
    providerId,
    type,
    origin,
    action,
    reason,
}: {
    providerId: string
    type: TAllIntegrations
    origin: TIntegrationOrigin
    action: TIntegrationActions
    reason?: string | null
}) => {
    Analytics.event('SEM::integration_failed', {
        integrationType: type,
        providerId,
        displayName: getProviderById(providerId)?.displayName,
        origin,
        action,
        reason,
    })

    IntegrationsStore.setIntegrationEventData(null)
}

const createIntegration = async (
    providerId: string,
    accountId: string,
    data: string,
    authType: string
): Promise<
    [
        (Error & AxiosError<unknown, any>) | undefined,
        IntegrationRequestResponseData | undefined,
    ]
> => {
    const [newIntegrationError, request_id] =
        await IntegrationsService.newIntegration(
            providerId,
            accountId,
            data,
            authType
        )

    if (newIntegrationError) {
        return [newIntegrationError, undefined]
    }

    const [getIntegrationError, result] =
        await IntegrationsService.getIntegrationRequest(request_id!)

    if (getIntegrationError) {
        return [getIntegrationError, undefined]
    }

    return [undefined, result]
}

const handleIntegration = async (
    providerId: string,
    data: string,
    authType: string,
    displayName: string,
    type: TAllIntegrations
) => {
    IntegrationsStore.updateProviderProperty(providerId, 'loading', true)
    const integrationEventData = IntegrationsStore.getIntegrationEventData()

    const currentAccountId = AccountModule.getCurrentAccountId()
    sendIntegrationCallEvent({
        providerId,
        type,
        origin: integrationEventData?.origin ?? 'integrations-page',
        action: integrationEventData?.action ?? 'add',
    })
    const [_, result] = await createIntegration(
        providerId,
        currentAccountId!,
        data,
        authType
    )
    if (result?.status === IntegrationRequestResponseData.status.COMPLETED) {
        const sendRejectedEvent = () =>
            sendIntegrationFailedEvent({
                providerId,
                type,
                origin: integrationEventData?.origin ?? 'integrations-page',
                action: integrationEventData?.action ?? 'add',
                reason: result.status_info,
            })

        if (result.status_info === 'EXTERNAL_IDENTIFIER_MISMATCH') {
            sendRejectedEvent()
            handleFailedToUpdateIntegration(providerId, type)
            return
        }
        if (result.status_info === 'EXISTING_ACTIVE_INTEGRATION') {
            sendRejectedEvent()
            handleExistingActiveIntegration(providerId, type)
            return
        }

        Analytics.event('SEM::integration_connected', {
            integrationType: type,
            providerId,
            displayName: getProviderById(providerId)?.displayName,
            authType,
            origin: integrationEventData?.origin ?? 'integrations-page',
            action: integrationEventData?.action ?? 'add',
        })
        ScansService.$eventBus.emit('scan_in_progress')
        generateIntegrationSuccessToast(result.status_info, type)
        IntercomService.sendEvent('fraud-defense-integration-activated', {
            integration_type: type,
        })

        await pollUpdatedData(false, providerId, displayName)
        IntegrationsStore.updateProviderProperty(providerId, 'loading', false)
    } else {
        sendIntegrationFailedEvent({
            providerId,
            type,
            origin: integrationEventData?.origin ?? 'integrations-page',
            action: integrationEventData?.action ?? 'add',
        })
        generateIntegrationErrorToast(result?.status_info, type)
        IntegrationsStore.updateProviderProperty(providerId, 'loading', false)
    }
}

const handleFailedToUpdateIntegration = (
    providerId: string,
    type: TAllIntegrations
): void => {
    generateToast({
        type: 'error',
        data: `Failed to update ${TOAST_INTEGRATION_TYPE_MAPPING[type]} account. Please verify details and try again.`,
        testId: 'toast-integration-update-failed',
    })
    IntegrationsStore.updateProviderProperty(providerId, 'loading', false)
}

const handleExistingActiveIntegration = (
    providerId: string,
    type: TAllIntegrations
): void => {
    generateToast({
        type: 'info',
        data: `Failed to add ${TOAST_INTEGRATION_TYPE_MAPPING[type]} account. Account already exists.`,
        testId: 'toast-integration-failed',
    })
    IntegrationsStore.updateProviderProperty(providerId, 'loading', false)
}

const generateIntegrationSuccessToast = (
    statusInfo: string | null | undefined,
    type: TAllIntegrations
) => {
    if (type === 'crowdstrike') return

    let message: string
    let testId: string
    switch (statusInfo) {
        case 'INTEGRATION_UPGRADED_SUCCESSFULLY':
            message = `Successfully updated ${TOAST_INTEGRATION_TYPE_MAPPING[type]} account. Cloud App Security and Fraud defense activated.`
            testId = 'toast-integration-updated'
            break
        default:
            message = `Successfully added ${TOAST_INTEGRATION_TYPE_MAPPING[type]} account. Cloud App Security and Fraud defense activated.`
            testId = 'toast-cloud-app-fraud-defense-activated'
    }

    generateToast({
        type: 'success',
        data: message,
        testId,
    })
}

const generateIntegrationErrorToast = (
    statusInfo: string | null | undefined,
    type: TAllIntegrations
) => {
    generateToast({
        type: 'error',
        data: getErrorMessage(statusInfo, type),
        testId: 'toast-integration-failed',
    })
}

const activateIntegration = async ({
    tenantId,
    providerId,
    code,
    custom,
}: {
    providerId: string
    code?: string
    custom?: { [key: string]: string }
    tenantId?: string
}) => {
    const displayName = `${getProviderById(providerId)?.displayName ?? 'The'} integration`
    const type = getProviderById(providerId)?.type!

    if (providerId && tenantId) {
        await handleIntegration(
            providerId,
            tenantId,
            'tenant_id',
            displayName,
            type
        )
    }

    if (providerId && code) {
        await handleIntegration(
            providerId,
            code,
            'auth_code',
            displayName,
            type
        )
    }

    if (custom) {
        const data_to_encrypt = JSON.stringify(custom)
        const pub_key = envVars.REACT_APP_CS_RSA_ENC_KEY
        let encrypted = ''
        try {
            encrypted = rsaEncrypt(pub_key, data_to_encrypt)
        } catch (e) {
            generateToast({
                type: 'error',
                data: `${displayName} was not successful, please try again later`,
            })
            Sentry.captureException(
                new Error(
                    `Unable to encrypt data for ${displayName}. Check encryption key`
                )
            )
            return
        }
        await handleIntegration(
            providerId,
            encrypted,
            'client_data',
            displayName,
            type
        )
    }
}

const getErrorMessage = (
    statusInfo: string | null | undefined,
    type: TAllIntegrations
): string => {
    if (statusInfo && statusInfo.includes(keyErrorInfo.maxUsersExceeded.key!)) {
        return keyErrorInfo.maxUsersExceeded.message
    }

    return `Failed to add ${TOAST_INTEGRATION_TYPE_MAPPING[type]} account. Please verify details and try again.`
}

const deleteFailed = ({
    providerId,
    type,
}: {
    providerId: string
    type: TAllIntegrations
}) => {
    if (!providerId) return
    IntegrationsStore.updateProviderProperty(providerId, 'loading', false)
    generateToast({
        type: 'error',
        data: `Failed to remove ${TOAST_INTEGRATION_TYPE_MAPPING[type]} account. Please try again.`,
    })
}

const pollDataIfNeeded = ({
    providerId,
    isWinClosed,
}: {
    providerId: string
    isWinClosed?: boolean // indication if deactivate by toggle or by window close
}) => {
    if (!providerId) return

    if (!isWinClosed) {
        // deactivate by toggle
        pollUpdatedData(true, providerId, 'Microsoft integration')
    }
}

const loadIntegrations = async (
    forced?: boolean
): Promise<TIntegrationProviders | boolean> => {
    if (forced || !IntegrationsStore.getStore().lastFetched) {
        IntegrationsStore.setLoading(true)

        const result = await IntegrationsService.getIntegrations()

        if (!result) {
            //TODO: add error handling & user messages/placeholders
            IntegrationsStore.setLoading(false)
            IntegrationsStore.setError(true)
            return false
        }
        IntegrationsStore.setProviders(result)
        IntegrationsStore.setLastFetched(Date.now())
        IntegrationsStore.setLoading(false)
        return result
    }

    return IntegrationsStore.getStore().providers
}

const pollUpdatedData = async (
    deactivate = false,
    providerId: string,
    integrationName?: string
) => {
    await loadIntegrations(true)
    // polling for new data after/before integrations.
    const issuesBeforeLength = IssuesModule.store.get().data.length

    await IntegrationsService.pollIntegrationsScans()

    await IssuesModule.loadIssues(true)
    const updatedIssuesLength = IssuesModule.store.get().data.length

    if (!deactivate) {
        generateToast({
            type: 'success',
            data: `Scan successfully completed. You can find the updated information in the My Stance page`,
            testId: 'toast-scan-completed',
        })

        const { type, integrations } = getProviderById(providerId)!

        if (!FRAUD_DEFENSE_INTEGRATION_TYPES.includes(type)) {
            if (issuesBeforeLength === updatedIssuesLength) {
                Analytics.event(
                    'SEM::polling_integration_issues_end_with_no_new_issues'
                )
                generateToast({
                    type: 'success',
                    data: `${integrationName} successfully set, with no issues found. Good job!`,
                    testId: 'toast-integration-set',
                })
            } else {
                const integrationsIssues = integrations
                    .map((integration) =>
                        IssuesModule.getIntegrationIssuesById(
                            type,
                            integration.integration_id
                        )
                    )
                    .flat()
                Analytics.event(
                    'SEM::polling_integration_issues_end_with_new_issues'
                )
                generateToast({
                    type: 'success',
                    data: `${integrationName} successfully set. ${
                        integrationsIssues.length > 0
                            ? integrationsIssues.length +
                              ' issues found - ' +
                              '<a href="/">see here</a>'
                            : ''
                    }`,
                    testId: 'toast-integration-set',
                })
            }
        } else {
            await loadIntegrations(true)
        }
    }
}

const pollIntegrationsScans = () => {
    IntegrationsService.pollIntegrationsScans(2000)
}

const removeIntegration = async (
    provider_id: string,
    integration_id: string | undefined,
    type: TAllIntegrations
) => {
    Analytics.event('SEM::user_call_delete_integration', {
        providerId: provider_id,
        integrationId: integration_id,
        type,
    })
    const [error, data] = await IntegrationsService.deleteIntegration(
        provider_id,
        integration_id
    )

    if (!error && data?.status === 204) {
        IntegrationsModule.pollDataIfNeeded({
            providerId: provider_id,
        })
        generateToast({
            type: 'info',
            data: `${TOAST_INTEGRATION_TYPE_MAPPING[type]} account removed.`,
            testId: 'toast-account-removed',
        })
    } else {
        IntegrationsModule.deleteFailed({ providerId: provider_id, type })
    }
}

const handleIntegrationToggle = async (integration: TIntegration) => {
    const { id: providerId, status, integration_id, type } = integration

    if (status === 'active') {
        await removeIntegration(providerId, integration_id, type)
    } else {
        return IntegrationsService.handleIntegrationLogin({
            providerId,
            type,
        })
    }
}

const breakIntegrationState = ({
    providerId,
    error,
}: {
    providerId: string
    error?: string
}) => {
    IntegrationsStore.updateProviderProperty(providerId, 'loading', false)
}

const updateSecurityService = async (
    providerId: string,
    integrationId: string,
    serviceId: string,
    serviceName: TSecurityServiceName,
    enable: boolean
) => {
    const [error] = await IntegrationsService.updateSecurityService(
        integrationId,
        serviceId,
        serviceName,
        enable
    )
    if (!error) {
        await integrationsModule.loadIntegrations(true)
        IntegrationsStore.updateSecurityServiceState(
            providerId,
            integrationId,
            serviceId,
            enable
                ? SecurityServiceStateEnum.ENABLED
                : SecurityServiceStateEnum.DISABLED
        )
        generateFraudDefenseStateToast(enable)
        Analytics.event('SEM::user_toggled_fraud_defense_service', {
            integration_id: integrationId,
            enable,
        })
    } else {
        generateToast({
            type: 'error',
            data: 'Failed to update Fraud Defense state, please try again later.',
        })
    }
}

const generateFraudDefenseStateToast = (enable: boolean) => {
    if (enable) {
        generateToast({
            type: 'success',
            data: 'Fraud Defense Enabled. Your Email will now be protected.',
            testId: 'toast-fraud-defense-enabled',
        })
    } else {
        generateToast({
            type: 'warning',
            data: 'Fraud Defense disabled. Your Email will not be protected.',
            testId: 'toast-fraud-defense-disabled',
        })
    }
}

export const integrationsModule = {
    handleIntegrationLogin,
    createIntegration,
    activateIntegration,
    getErrorMessage,
    deleteFailed,
    pollDataIfNeeded,
    loadIntegrations,
    pollIntegrationsScans,
    handleIntegrationToggle,
    breakIntegrationState,
    removeIntegration,
    updateSecurityService,
}
